import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useState } from 'react';
import { Wallet, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import badge1 from '../../../../public/images/homePage/badge1.png';
import badge2 from '../../../../public/images/homePage/badge2.png';
import badge3 from '../../../../public/images/homePage/badge3.png';
import badge4 from '../../../../public/images/homePage/badge4.png';
import { Base_url } from '@app/config';

export function NoWallet() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Development bypass handler
  const handleDevBypass = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Development bypass: Skipping OAuth verification');
      const devToken = 'dev-token-' + Date.now();
      const devEmail = 'amit@go2glory.com'; // Your email from the error
      
      localStorage.setItem('authToken', devToken);
      localStorage.setItem('authType', 'gmail'); // Match the production auth type
      localStorage.setItem('userEmail', devEmail);
      
      // Redirect to main dashboard
      window.location.href = '/';
      
    } catch (err: any) {
      console.error('Development bypass error:', err);
      setError(err.message || 'Failed to authenticate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${Base_url}auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Your email is not authorized to access this system. Please contact support.');
        }
        throw new Error(data.message || 'Authentication failed');
      }

      if (!data.user || !data.user.email) {
        throw new Error('User email not returned from server');
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('authType', 'gmail');
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('userName', data.user.name || 'User');
      if (data.user.own_user_id) {
        localStorage.setItem('own_user_id', data.user.own_user_id.toString());
      }

      // Fetch and store party_id
      try {
        const partyIdResponse = await fetch(`${Base_url}party-id/${encodeURIComponent(data.user.email)}`);
        if (partyIdResponse.ok) {
          const partyData = await partyIdResponse.json();
          if (partyData.party_id) {
            localStorage.setItem('party_id', partyData.party_id);
            console.log('✅ Party ID stored:', partyData.party_id);
          }
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch party_id:', err);
      }

      // Redirect to main dashboard after successful Google login
      window.location.href = '/';

    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message || 'Failed to authenticate with Google');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again.');
  };

  return (
    <div className="auth-page-container">
      {/* Floating Blobs */}
      <div className="auth-floating-blob auth-floating-blob-purple"></div>
      <div className="auth-floating-blob auth-floating-blob-cyan"></div>

      {/* Main Content Container */}
      <div className="auth-content-wrapper">

        {/* HERO SECTION */}
        <div className="auth-hero-section">
          <h1 className="auth-hero-title">
            AI Powered Digital Signature Platform
          </h1>
          <p className="auth-hero-subtitle">
            Choose your authentication method
          </p>
          <p className="auth-hero-text">
            For assistance or security inquiries, contact{' '}
            <a href="mailto:help@go2glory.com" className="auth-link">
              help@go2glory.com
            </a>
            {' '}or{' '}
            <a
              href="https://go2glory.com/contact-tech-support/"
              className="auth-link auth-link-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              schedule an appointment
            </a>
          </p>
          <p className="auth-hero-text">
            Connect your digital wallet or sign in with Google to verify your credentials and access our protected portal.
          </p>
          
          {/* DEVELOPMENT MODE INDICATOR */}
          {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              color: '#856404',
              padding: '10px',
              borderRadius: '6px',
              margin: '10px 0',
              fontSize: '14px'
            }}>
              <strong>🔧 Development Mode:</strong> OAuth bypass enabled. Click any Google Sign-In button to continue.
            </div>
          )}
          
          <p className="auth-hero-text auth-hero-text-last">
            Our authentication process adheres to industry-leading security protocols and has been fully vetted by independent cybersecurity experts. Your wallet information remains encrypted and is never stored on our servers. This verification step ensures that only authorized users can access sensitive information and premium features within our ecosystem.
          </p>
        </div>

        {/* AUTH CARDS GRID */}
        <div className="auth-cards-grid">

          {/* WALLET CARD */}
          <div className="auth-card-wrapper">
            <div className="auth-card-glow auth-card-glow-purple"></div>
            <div className="auth-card">
              <div className="auth-card-header">
                <h3 className="auth-card-title">Crypto Wallet</h3>
                <div className="auth-card-icon auth-card-icon-purple">
                  <Wallet className="auth-icon" />
                </div>
              </div>

              <p className="auth-card-description">
                Connect your MetaMask, WalletConnect, or any Web3-compatible wallet for secure blockchain authentication.
              </p>

              <div className="auth-button-wrapper">
                {/* @ts-ignore */}
                <w3m-button />
              </div>

              <div className="auth-card-features">
                <div className="auth-feature-item">
                  <CheckCircle2 className="auth-feature-icon" />
                  <span>Decentralized & Secure</span>
                </div>
                <div className="auth-feature-item">
                  <CheckCircle2 className="auth-feature-icon" />
                  <span>No Password Required</span>
                </div>
              </div>
            </div>
          </div>

          {/* GOOGLE CARD */}
          <div className="auth-card-wrapper">
            <div className="auth-card-glow auth-card-glow-cyan"></div>
            <div className="auth-card">
              <div className="auth-card-header">
                <h3 className="auth-card-title">Google Account</h3>
                <div className="auth-card-icon auth-card-icon-white">
                  <svg className="auth-google-icon" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                  </svg>
                </div>
              </div>

              <p className="auth-card-description">
                Quick and easy authentication using your existing Google account. No additional setup required.
              </p>

              {error && (
                <div className="auth-error-box">
                  <div className="auth-error-content">
                    <AlertCircle className="auth-error-icon" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <div className="auth-button-wrapper">
                {isLoading ? (
                  <div className="auth-loading">
                    <Loader2 className="auth-loading-icon" />
                    <span className="auth-loading-text">Authenticating...</span>
                  </div>
                ) : (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? (
                  <button
                    onClick={handleDevBypass}
                    style={{
                      background: 'linear-gradient(135deg, #4285f4, #34a853)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(66, 133, 244, 0.3)'
                    }}
                  >
                    <span>🚀</span>
                    Continue to Dashboard (Dev Mode)
                  </button>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline"
                    size="large"
                    text="signin_with"
                    shape="pill"
                    logo_alignment="center"
                    width="300"
                  />
                )}
              </div>

              <div className="auth-card-features">
                <div className="auth-feature-item">
                  <CheckCircle2 className="auth-feature-icon" />
                  <span>One-Click Sign In</span>
                </div>
                <div className="auth-feature-item">
                  <CheckCircle2 className="auth-feature-icon" />
                  <span>Trusted by Millions</span>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* FOOTER */}
      <footer className="auth-footer">
        <div className="auth-footer-container">
          <div className="auth-footer-content">
            <div className="auth-footer-badges">
              <img className="auth-badge" src={badge1} alt="badge1" />
              <img className="auth-badge" src={badge2} alt="badge2" />
              <img className="auth-badge auth-badge-small" src={badge3} alt="badge3" />
              <img className="auth-badge auth-badge-medium" src={badge4} alt="badge4" />
            </div>

            <div className="auth-footer-links">
              <a className="auth-footer-link" href="https://go2glory.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Help
              </a>
              <a className="auth-footer-link" href="https://go2glory.com/terms-of-use" target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <a className="auth-footer-link" href="/">
                Terms
              </a>
            </div>
          </div>

          <p className="auth-footer-copyright">
            Go 2 Glory Limited (Company No. 15720361), registered at 85 Great Portland Street, First Floor, London W1W 7LT. © Go 2 Glory Limited.
          </p>
        </div>
      </footer>

    </div>
  );
}
